import React, { FormEvent, useMemo, useRef, useState } from 'react';
import { BuildingIcon, CheckCircle2Icon, ClockIcon, FileSignatureIcon, FileTextIcon, LayoutGridIcon, ListIcon, LoaderCircleIcon, MailIcon, PhoneIcon, PlusIcon, SearchIcon, SendHorizontalIcon, UserRoundIcon, XIcon } from 'lucide-react';
import { askCarli, AgentMessage } from '../utils/carliAgent';
type CompanyType = 'Brand' | '3PL partner' | 'Strategic partner' | 'Portfolio company';
type Stage = 'Lead' | 'Qualified' | 'Contract sent' | 'Signed' | 'Activated';
type ContractStatus = 'None' | 'Sent' | 'Signed';
type Company = {
  id: string;
  name: string;
  domain: string;
  type: CompanyType;
  stage: Stage;
  owner: string;
  contract: ContractStatus;
  value: string;
  lastActivity: string;
  location: string;
};
type Activity = {
  id: string;
  company: string;
  kind: 'Email' | 'Contract' | 'Call' | 'Note';
  summary: string;
  owner: string;
  timestamp: string;
};
type ChatEntry = {
  id: number;
  author: 'carli' | 'user';
  text: string;
  citation?: string;
};
const stages: Stage[] = ['Lead', 'Qualified', 'Contract sent', 'Signed', 'Activated'];
const companyTypes: CompanyType[] = ['Brand', '3PL partner', 'Strategic partner', 'Portfolio company'];
const stageTone: Record<Stage, string> = {
  Lead: 'bg-ink/10 text-ink/60',
  Qualified: 'bg-[#4D9CC7]/15 text-[#2F6E8F]',
  'Contract sent': 'bg-[#C9A04E]/18 text-[#8B6A20]',
  Signed: 'bg-[#BB5E40]/12 text-[#94452E]',
  Activated: 'bg-forest/12 text-forest'
};
const typeTone: Record<CompanyType, string> = {
  Brand: 'bg-[#BB5E40]/10 text-[#94452E]',
  '3PL partner': 'bg-[#4D9CC7]/12 text-[#2F6E8F]',
  'Strategic partner': 'bg-[#A77ACA]/14 text-[#6B4A8C]',
  'Portfolio company': 'bg-forest/10 text-forest'
};
const initialCompanies: Company[] = [{
  id: 'eddy',
  name: 'Eddy',
  domain: 'eddy.com',
  type: 'Brand',
  stage: 'Activated',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '$210,600',
  lastActivity: '12m',
  location: 'New York, US'
}, {
  id: 'one-people',
  name: '1 People',
  domain: '1people.com',
  type: 'Brand',
  stage: 'Activated',
  owner: 'Meg Sawtelle',
  contract: 'Signed',
  value: '$96,400',
  lastActivity: '1h',
  location: 'Copenhagen, DK'
}, {
  id: 'nature-baby',
  name: 'Nature Baby',
  domain: 'naturebaby.com',
  type: 'Brand',
  stage: 'Signed',
  owner: 'Meg Sawtelle',
  contract: 'Signed',
  value: '$68,200',
  lastActivity: '3h',
  location: 'Auckland, NZ'
}, {
  id: 'matdalee',
  name: 'MATDALEE Studio',
  domain: 'matdalee.com',
  type: 'Brand',
  stage: 'Signed',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '$41,800',
  lastActivity: 'Yesterday',
  location: 'Los Angeles, US'
}, {
  id: 'seaesta',
  name: 'SEAESTA SURF',
  domain: 'seaestasurf.com',
  type: 'Brand',
  stage: 'Contract sent',
  owner: 'Meg Sawtelle',
  contract: 'Sent',
  value: '$52,000',
  lastActivity: '2d',
  location: 'San Diego, US'
}, {
  id: 'lili-blanc',
  name: 'Lili Blanc',
  domain: 'liliblanc.com',
  type: 'Brand',
  stage: 'Contract sent',
  owner: 'Meris Butler',
  contract: 'Sent',
  value: '$34,500',
  lastActivity: '2d',
  location: 'Dubai, AE'
}, {
  id: 'hermoza',
  name: 'Hermoza',
  domain: 'hermoza.com',
  type: 'Brand',
  stage: 'Qualified',
  owner: 'Meg Sawtelle',
  contract: 'None',
  value: '$28,900',
  lastActivity: '4d',
  location: 'Washington, US'
}, {
  id: 'no-more-nobody',
  name: 'No More Nobody',
  domain: 'nomorenobody.com',
  type: 'Brand',
  stage: 'Qualified',
  owner: 'Meris Butler',
  contract: 'None',
  value: '$22,400',
  lastActivity: '5d',
  location: 'London, UK'
}, {
  id: 'arkitaip',
  name: 'arkitaip',
  domain: 'arkitaip.com',
  type: 'Brand',
  stage: 'Lead',
  owner: 'Meg Sawtelle',
  contract: 'None',
  value: '$18,000',
  lastActivity: '1w',
  location: 'London, UK'
}, {
  id: 'il-gufo',
  name: 'Il Gufo',
  domain: 'ilgufo.com',
  type: 'Brand',
  stage: 'Lead',
  owner: 'Meris Butler',
  contract: 'None',
  value: '$46,000',
  lastActivity: '1w',
  location: 'Asolo, IT'
}, {
  id: 'shipbob',
  name: 'ShipBob',
  domain: 'shipbob.com',
  type: '3PL partner',
  stage: 'Activated',
  owner: 'Rich Amsinger',
  contract: 'Signed',
  value: '—',
  lastActivity: '3d',
  location: 'Chicago, US'
}, {
  id: 'flowspace',
  name: 'Flowspace',
  domain: 'flow.space',
  type: '3PL partner',
  stage: 'Contract sent',
  owner: 'Rich Amsinger',
  contract: 'Sent',
  value: '—',
  lastActivity: '6d',
  location: 'Los Angeles, US'
}, {
  id: 'poshmark-partner',
  name: 'Poshmark',
  domain: 'poshmark.com',
  type: 'Strategic partner',
  stage: 'Activated',
  owner: 'Meris Butler',
  contract: 'Signed',
  value: '—',
  lastActivity: '1d',
  location: 'Redwood City, US'
}, {
  id: 'whatnot-partner',
  name: 'Whatnot',
  domain: 'whatnot.com',
  type: 'Strategic partner',
  stage: 'Qualified',
  owner: 'Meris Butler',
  contract: 'None',
  value: '—',
  lastActivity: '1w',
  location: 'Marina del Rey, US'
}, {
  id: 'bloomy',
  name: 'Bloomy Collective',
  domain: 'bloomycollective.com',
  type: 'Portfolio company',
  stage: 'Signed',
  owner: 'Rich Amsinger',
  contract: 'Signed',
  value: '$12,600',
  lastActivity: '2w',
  location: 'Austin, US'
}];
const initialActivity: Activity[] = [{
  id: 'a1',
  company: 'Eddy',
  kind: 'Email',
  summary: 'Sent Spring Collection recovery report and nine-month plan.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 9:42 AM'
}, {
  id: 'a2',
  company: 'SEAESTA SURF',
  kind: 'Contract',
  summary: 'Recovery agreement sent for signature.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 8:15 AM'
}, {
  id: 'a3',
  company: 'Nature Baby',
  kind: 'Note',
  summary: 'Unit counts on summer submission need confirmation.',
  owner: 'Meg Sawtelle',
  timestamp: 'Today, 6:58 AM'
}, {
  id: 'a4',
  company: 'Lili Blanc',
  kind: 'Email',
  summary: 'Follow-up on excess inventory volume for Q3.',
  owner: 'Meris Butler',
  timestamp: 'Yesterday, 4:20 PM'
}, {
  id: 'a5',
  company: 'MATDALEE Studio',
  kind: 'Contract',
  summary: 'Agreement countersigned — ready for onboarding.',
  owner: 'Meris Butler',
  timestamp: 'Yesterday, 11:02 AM'
}, {
  id: 'a6',
  company: 'Flowspace',
  kind: 'Call',
  summary: 'Discussed intake SLAs and returns processing fees.',
  owner: 'Rich Amsinger',
  timestamp: 'Monday, 2:30 PM'
}];
const suggestedPrompts = ['Which brands have signed but not activated?', 'Email Lili Blanc about more excess inventory', 'Add a new company', 'What contracts are waiting on signature?'];
export function PipelineDashboard() {
  const [companies, setCompanies] = useState(initialCompanies);
  const [activity, setActivity] = useState(initialActivity);
  const [view, setView] = useState<'board' | 'companies' | 'activity'>('board');
  const [typeFilter, setTypeFilter] = useState<CompanyType | 'All'>('All');
  const [query, setQuery] = useState('');
  const [prompt, setPrompt] = useState('');
  const [chat, setChat] = useState<ChatEntry[]>([{
    id: 1,
    author: 'carli',
    text: 'Good morning, Rich. There are 4 companies signed but not yet activated, 3 contracts waiting on signature, and 2 leads with no activity in over a week. What would you like to work on?',
    citation: 'Pipeline · Contracts · Activity'
  }]);
  const [createOpen, setCreateOpen] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    domain: '',
    type: 'Brand' as CompanyType,
    owner: 'Meris Butler',
    location: '',
    value: ''
  });
  const chatIdRef = useRef(2);
  const filteredCompanies = useMemo(() => companies.filter(company => {
    const matchesType = typeFilter === 'All' || company.type === typeFilter;
    const matchesQuery = company.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
    return matchesType && matchesQuery;
  }), [companies, typeFilter, query]);
  const signedNotActivated = companies.filter(company => company.stage === 'Signed');
  const contractsOut = companies.filter(company => company.contract === 'Sent');
  const applyIntent = (text: string) => {
    const lower = text.toLocaleLowerCase();
    if ((lower.includes('add') || lower.includes('create')) && lower.includes('compan')) {
      setCreateOpen(true);
      return 'Pipeline · Companies';
    }
    if (lower.includes('signed') && (lower.includes('not activated') || lower.includes("hasn't activated") || lower.includes('no activation'))) {
      setView('companies');
      setTypeFilter('All');
      return 'Pipeline · Contract status';
    }
    if (lower.includes('contract')) {
      setView('activity');
      return 'Contracts · Last activity';
    }
    if (lower.includes('email')) {
      const matched = companies.find(company => lower.includes(company.name.toLocaleLowerCase()));
      const target = matched?.name ?? 'the account';
      setActivity(current => [{
        id: `draft-${Date.now()}`,
        company: target,
        kind: 'Email',
        summary: 'Carli drafted an outreach email for review.',
        owner: 'Carli · pending review',
        timestamp: 'Just now'
      }, ...current]);
      return 'Activity · Email draft';
    }
    return 'Pipeline assistant';
  };
  const pipelineContext = () => ['You are the sales pipeline assistant for the Manymoons revenue team. The user is a sales rep working their book of business.', 'You can pull lists, summarize pipeline health, draft outreach emails for approval, and create company records. Any email you draft is logged for human approval before it sends — say so.', `Pipeline (${companies.length} companies), formatted as name | category | stage | contract | owner | opportunity | last activity:`, ...companies.map(company => `- ${company.name} | ${company.type} | ${company.stage} | contract ${company.contract} | ${company.owner} | ${company.value} | last activity ${company.lastActivity}`), '', 'Recent activity log:', ...activity.slice(0, 8).map(item => `- ${item.timestamp} · ${item.company} · ${item.kind} · ${item.summary} (${item.owner})`), '', 'Stages run Lead → Qualified → Contract sent → Signed → Activated. "Signed" means the contract is executed but the brand has not submitted inventory yet.', 'Answer using only the pipeline above. When drafting an email, write the actual subject line and body copy.'].join('\n');
  const runPrompt = async (text: string) => {
    if (thinking) return;
    const citation = applyIntent(text);
    const history: AgentMessage[] = [...chat.map(entry => ({
      role: entry.author === 'user' ? 'user' as const : 'assistant' as const,
      content: entry.text
    })), {
      role: 'user',
      content: text
    }];
    setChat(current => [...current, {
      id: chatIdRef.current++,
      author: 'user',
      text
    }]);
    setPrompt('');
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context: pipelineContext(),
        maxTokens: 900
      });
      setChat(current => [...current, {
        id: chatIdRef.current++,
        author: 'carli',
        text: reply,
        citation
      }]);
    } catch (caught) {
      setChat(current => [...current, {
        id: chatIdRef.current++,
        author: 'carli',
        text: (caught as Error).message
      }]);
    } finally {
      setThinking(false);
    }
  };
  const submitPrompt = (event: FormEvent) => {
    event.preventDefault();
    const text = prompt.trim();
    if (text) void runPrompt(text);
  };
  const createCompany = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    setCompanies(current => [{
      id: draft.name.toLocaleLowerCase().replace(/\s+/g, '-'),
      name: draft.name.trim(),
      domain: draft.domain.trim() || `${draft.name.trim().toLocaleLowerCase().replace(/\s+/g, '')}.com`,
      type: draft.type,
      stage: 'Lead',
      owner: draft.owner,
      contract: 'None',
      value: draft.value.trim() || '—',
      lastActivity: 'Just now',
      location: draft.location.trim() || '—'
    }, ...current]);
    setChat(current => [...current, {
      id: chatIdRef.current++,
      author: 'carli',
      text: `${draft.name.trim()} is now in the pipeline as a ${draft.type.toLocaleLowerCase()} at the Lead stage, assigned to ${draft.owner}.`,
      citation: 'Pipeline · Companies'
    }]);
    setDraft({
      name: '',
      domain: '',
      type: 'Brand',
      owner: 'Meris Butler',
      location: '',
      value: ''
    });
    setCreateOpen(false);
    setView('companies');
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="pipeline-heading" data-id="element-1642" data-mp="2e6sb7x4dg4gn">
      <section className="rounded-3xl border border-[#BB5E40]/20 bg-[#BB5E40]/5 p-5 sm:p-6" aria-labelledby="pipeline-heading" data-id="element-1643" data-mp="2j5wmquerqea5">
        <div className="flex items-center justify-between gap-4" data-id="element-1644" data-mp="256wy3mr99zdz">
          <div className="flex items-center gap-2" data-id="element-1645" data-mp="2a56q5ohoojvd">
            <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-[#BB5E40]" style={{
            WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
            maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain'
          }} data-id="element-1646" data-mp="2a3n9vxw128tl" />
            <h1 id="pipeline-heading" className="text-sm font-semibold" data-id="element-1647" data-mp="xura2yci8pgn">Ask Carli</h1>
          </div>
          <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1648" data-mp="svo49bopaf7m">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1649" data-mp="12vx4d0ltcksa" />
            Create company
          </button>
        </div>

        <ul className="mt-4 space-y-3" aria-live="polite" data-id="element-1650" data-mp="1buih2f4lt5l5">
          {chat.slice(-4).map(entry => <li key={entry.id} className={entry.author === 'user' ? 'flex justify-end' : ''} data-id="element-1651" data-mp="1qj58vgbf3pt9">
              <div className={`max-w-2xl whitespace-pre-line rounded-2xl px-4 py-3 text-sm leading-relaxed ${entry.author === 'user' ? 'bg-[#BB5E40] text-cream' : 'bg-paper text-ink/75 shadow-sm'}`} data-id="element-1652" data-mp="1lkbw5q3yg2u5">
                {entry.text}
                {entry.citation && <p className="mt-2 text-[11px] uppercase tracking-wider text-ink/40" data-id="element-1653" data-mp="1vjszi0vqi4my">{entry.citation}</p>}
              </div>
            </li>)}
          {thinking && <li className="flex items-center gap-2 text-xs text-ink/50" data-id="element-1654" data-mp="3tz9v76bd0x5f">
              <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-1655" data-mp="2lw1wirjgdk4" />
              Carli is thinking…
            </li>}
        </ul>

        <form onSubmit={submitPrompt} className="mt-4" data-id="element-1656" data-mp="7mjo8r4to680">
          <label htmlFor="pipeline-prompt" className="sr-only" data-id="element-1657" data-mp="363nykagmdlon">Ask Carli about your pipeline</label>
          <div className="flex items-center gap-2 rounded-2xl border border-[#BB5E40]/30 bg-paper p-2 pl-4 focus-within:border-[#BB5E40] focus-within:ring-2 focus-within:ring-[#BB5E40]/15" data-id="element-1658" data-mp="3b28nveuc2kyb">
            <input id="pipeline-prompt" value={prompt} onChange={event => setPrompt(event.target.value)} placeholder="Ask Carli to pull a list, draft an email, or add a company…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-1659" data-mp="3k36snzcliwhy" />
            <button type="submit" disabled={!prompt.trim() || thinking} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#BB5E40] px-3.5 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" data-id="element-1660" data-mp="2fsc2op8sjchf">
              {thinking ? <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-1661" data-mp="2148wpfur00sm" /> : <SendHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1662" data-mp="1w43boiizhcji" />}
              Send
            </button>
          </div>
        </form>

        <div className="mt-3 flex flex-wrap gap-2" data-id="element-1663" data-mp="1s4vn8n6mjc7w">
          {suggestedPrompts.map(item => <button key={item} type="button" onClick={() => void runPrompt(item)} className="rounded-full border border-[#BB5E40]/25 bg-paper px-3 py-1.5 text-xs font-medium text-ink/70 transition-colors hover:border-[#BB5E40]/50 hover:text-[#94452E]" data-id="element-1664" data-mp="1n5rg0239fok7">
              {item}
            </button>)}
        </div>
      </section>

      <section className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Pipeline summary" data-id="element-1665" data-mp="99hovwcpkjmz">
        <SummaryCard icon={BuildingIcon} label="Companies" value={`${companies.length}`} detail="Across all categories" data-id="element-1666" data-mp="3rjtbfmzxi2r0" />
        <SummaryCard icon={FileSignatureIcon} label="Contracts out" value={`${contractsOut.length}`} detail="Awaiting signature" data-id="element-1667" data-mp="o4e44tixq79k" />
        <SummaryCard icon={CheckCircle2Icon} label="Signed, not activated" value={`${signedNotActivated.length}`} detail="Ready for onboarding" data-id="element-1668" data-mp="s57stp47bq3i" />
        <SummaryCard icon={ClockIcon} label="Open pipeline" value="$372k" detail="Estimated recovery value" data-id="element-1669" data-mp="e944gcg0ebyy" />
      </section>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1670" data-mp="2ywfli8une7ge">
        <div className="flex gap-1 rounded-xl border border-ink/10 bg-cream p-1" data-id="element-1671" data-mp="33wl7p9i5f1r3">
          {([['board', 'Board', LayoutGridIcon], ['companies', 'Companies', ListIcon], ['activity', 'Activity', MailIcon]] as const).map(([id, label, Icon]) => <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === id ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1672" data-mp="2rd6daixvoesx">
              <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1673" data-mp="28nzpwim52z0s" />
              {label}
            </button>)}
        </div>

        {view !== 'activity' && <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-1674" data-mp="2hnwkf0v7ms3r">
            <div className="relative" data-id="element-1675" data-mp="2hljvu4uy5jhq">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1676" data-mp="1txk6989dfz4q" />
              <label htmlFor="pipeline-search" className="sr-only" data-id="element-1677" data-mp="1oypc5a5yeo5b">Search companies</label>
              <input id="pipeline-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search companies" className="w-full rounded-xl border border-ink/15 bg-paper py-2.5 pl-8 pr-3 text-sm text-ink outline-none placeholder:text-ink/40 focus:border-[#BB5E40]/35 sm:w-56" data-id="element-1678" data-mp="14eqamv9ntyjg" />
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-1679" data-mp="1rdd12pttdl97">
              {(['All', ...companyTypes] as const).map(item => <button key={item} type="button" onClick={() => setTypeFilter(item)} aria-pressed={typeFilter === item} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${typeFilter === item ? 'bg-ink text-cream' : 'border border-ink/15 bg-paper text-ink/60 hover:text-ink'}`} data-id="element-1680" data-mp="1w41sk55hui8c">
                  {item}
                </button>)}
            </div>
          </div>}
      </div>

      {view === 'board' && <section className="mt-5 -mx-2 overflow-x-auto px-2 pb-2" aria-label="Pipeline board" data-id="element-1681" data-mp="3llgung3ptkdk">
          <div className="grid min-w-[900px] grid-cols-5 gap-3" data-id="element-1682" data-mp="3lna0it6i80ki">
            {stages.map(stage => {
          const stageCompanies = filteredCompanies.filter(company => company.stage === stage);
          return <div key={stage} className="rounded-2xl border border-ink/10 bg-cream/45 p-3" data-id="element-1683" data-mp="d53yjr77hwln">
                  <div className="flex items-center justify-between gap-2" data-id="element-1684" data-mp="8ef1zxgo69fn">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink/55" data-id="element-1685" data-mp="3fu1dwxi9nqo">{stage}</h3>
                    <span className="rounded-full bg-paper px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-1686" data-mp="10uy7hxoen10m">{stageCompanies.length}</span>
                  </div>
                  <ul className="mt-3 space-y-2" data-id="element-1687" data-mp="w36r2fyyqbo7">
                    {stageCompanies.map(company => <li key={company.id} data-id="element-1688" data-mp="2mn91k6d1dsbm">
                        <article className="rounded-xl border border-ink/10 bg-paper p-3" data-id="element-1689" data-mp="2hpzegxd4dsqy">
                          <p className="text-sm font-semibold text-ink" data-id="element-1690" data-mp="2do5i319yjawl">{company.name}</p>
                          <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-1691" data-mp="28pu7upfr2ydu">{company.domain}</p>
                          <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeTone[company.type]}`} data-id="element-1692" data-mp="35ftnf3ubix5x">{company.type}</span>
                          <div className="mt-3 flex items-center justify-between gap-2 border-t border-ink/10 pt-2 text-[11px] text-ink/55" data-id="element-1693" data-mp="31f1gih8x54ct">
                            <span className="truncate" data-id="element-1694" data-mp="2rool1tzkfqrw">{company.owner}</span>
                            <span className="shrink-0 font-semibold text-ink" data-id="element-1695" data-mp="2rmly5itpmdre">{company.value}</span>
                          </div>
                        </article>
                      </li>)}
                    {stageCompanies.length === 0 && <li className="rounded-xl border border-dashed border-ink/15 px-2 py-6 text-center text-[11px] text-ink/40" data-id="element-1696" data-mp="3rp1xp7lrkle5">No companies</li>}
                  </ul>
                </div>;
        })}
          </div>
        </section>}

      {view === 'companies' && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-label="Companies" data-id="element-1697" data-mp="9j5eg0uiehlk">
          <div className="-mx-2 overflow-x-auto px-2" data-id="element-1698" data-mp="egxe5h1eb80b">
            <table className="w-full min-w-[820px] text-left text-xs" data-id="element-1699" data-mp="jgtg7aqj8pd8">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1700" data-mp="jeqkca2p820l">
                <tr data-id="element-1701" data-mp="o8us2vjsge2q">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1702" data-mp="o8biq387ey0b">Company</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1703" data-mp="t5bglquwgugc">Category</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1704" data-mp="x8zns8fmdo12">Stage</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1705" data-mp="127azypte0amg">Contract</th>
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1706" data-mp="12586fvt0zzcz">Owner</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1707" data-mp="2yaz519z46t4p">Opportunity</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1708" data-mp="2ua5fyd9xr8a8">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1709" data-mp="3823mcgc9iq2p">
                {filteredCompanies.map(company => <tr key={company.id} data-id="element-1710" data-mp="3hyf2jhez402f">
                    <th scope="row" className="py-3 pr-3 text-left font-medium text-ink" data-id="element-1711" data-mp="3hx3w19u58e7m">
                      <span className="flex items-center gap-2.5" data-id="element-1712" data-mp="3cyahrf718j6e">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cream text-[10px] font-bold text-ink/60" data-id="element-1713" data-mp="3qyv0ycgv0h0f">{company.name.slice(0, 2).toLocaleUpperCase()}</span>
                        <span className="min-w-0" data-id="element-1714" data-mp="1wku39ux1q01x">
                          <span className="block truncate" data-id="element-1715" data-mp="1wlly33itjtr5">{company.name}</span>
                          <span className="block truncate text-[11px] font-normal text-ink/45" data-id="element-1716" data-mp="1rn2ayzshobx7">{company.location}</span>
                        </span>
                      </span>
                    </th>
                    <td className="py-3 pr-3" data-id="element-1717" data-mp="c204is9dhxpd"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeTone[company.type]}`} data-id="element-1718" data-mp="c29re6z4foqu">{company.type}</span></td>
                    <td className="py-3 pr-3" data-id="element-1719" data-mp="2czxqmompy0m"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${stageTone[company.stage]}`} data-id="element-1720" data-mp="2c6rrnxhvcjj">{company.stage}</span></td>
                    <td className="py-3 pr-3 text-ink/70" data-id="element-1721" data-mp="3kl7lg9st58b8">
                      <span className="inline-flex items-center gap-1.5" data-id="element-1722" data-mp="3piqzp9pi9l36">
                        {company.contract === 'Signed' ? <CheckCircle2Icon className="h-3.5 w-3.5 text-forest" aria-hidden="true" data-id="element-1723" data-mp="3bjrao0y2z46h" /> : company.contract === 'Sent' ? <FileTextIcon className="h-3.5 w-3.5 text-[#C9A04E]" aria-hidden="true" data-id="element-1724" data-mp="3fka1pss5g67m" /> : <span className="h-1.5 w-1.5 rounded-full bg-ink/20" aria-hidden="true" data-id="element-1725" data-mp="19rv12wvh44ec" />}
                        {company.contract}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-ink/70" data-id="element-1726" data-mp="p0pqu82d1mqm">{company.owner}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1727" data-mp="k24pu6faaz14">{company.value}</td>
                    <td className="py-3 text-right whitespace-nowrap text-ink/55" data-id="element-1728" data-mp="f31s771mvv36">{company.lastActivity}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {filteredCompanies.length === 0 && <p className="py-8 text-center text-sm text-ink/55" data-id="element-1729" data-mp="64g7b58ow3z8">No companies match this filter.</p>}
        </section>}

      {view === 'activity' && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-label="Activity log" data-id="element-1730" data-mp="3shy74nhfwej8">
          <h2 className="text-base font-semibold" data-id="element-1731" data-mp="3nkplgcx31sd8">Recent activity</h2>
          <p className="mt-1 text-xs text-ink/55" data-id="element-1732" data-mp="3iktgaacnfuw3">Emails, contracts, calls, and notes across every account.</p>
          <ol className="mt-5 space-y-2" data-id="element-1733" data-mp="3eigdnut78d3j">
            {activity.map(item => <li key={item.id} className="flex gap-3 rounded-xl border border-ink/10 bg-cream/40 p-4" data-id="element-1734" data-mp="1u176c8wjndkm">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-paper" data-id="element-1735" data-mp="1y1pyy569gnnv">
                  {item.kind === 'Email' ? <MailIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1736" data-mp="231vjz2i0zbwz" /> : item.kind === 'Contract' ? <FileSignatureIcon className="h-4 w-4 text-[#C9A04E]" aria-hidden="true" data-id="element-1737" data-mp="27zx31nfgbb8g" /> : item.kind === 'Call' ? <PhoneIcon className="h-4 w-4 text-[#4D9CC7]" aria-hidden="true" data-id="element-1738" data-mp="1ae9hxyog4fc8" /> : <UserRoundIcon className="h-4 w-4 text-ink/45" aria-hidden="true" data-id="element-1739" data-mp="1fazyen45xcy6" />}
                </span>
                <div className="min-w-0" data-id="element-1740" data-mp="1k9tbwfjg93yc">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-1741" data-mp="1k8rp5dt2mt0s">
                    <p className="text-sm font-semibold text-ink" data-id="element-1742" data-mp="1p2cmr4qr5kzg">{item.company}</p>
                    <span className="rounded-full bg-ink/7 px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-1743" data-mp="rl5n49zhrwg1">{item.kind}</span>
                    <span className="text-[11px] text-ink/45" data-id="element-1744" data-mp="wjfiolp4oz26">{item.timestamp}</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-1745" data-mp="oihl8j4ryzuk">{item.summary}</p>
                  <p className="mt-1 text-[11px] text-ink/45" data-id="element-1746" data-mp="kiqozzwhbrin">{item.owner}</p>
                </div>
              </li>)}
          </ol>
        </section>}

      {createOpen && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/45 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="create-company-heading" data-id="element-1747" data-mp="tc2n4m9hodz1">
          <button type="button" className="absolute inset-0 cursor-default" onClick={() => setCreateOpen(false)} aria-label="Close create company" data-id="element-1748" data-mp="1lfz35wqmafv" />
          <form onSubmit={createCompany} className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-paper p-6 shadow-2xl" data-id="element-1749" data-mp="akt15dij7wqk">
            <div className="flex items-start justify-between gap-4" data-id="element-1750" data-mp="1qy00y6cxbv4n">
              <div data-id="element-1751" data-mp="1m086p5ot3wxa">
                <h2 id="create-company-heading" className="text-lg font-semibold" data-id="element-1752" data-mp="1m0re3sgtz2x2">Create company</h2>
                <p className="mt-1 text-xs text-ink/55" data-id="element-1753" data-mp="33xbilvxi80j6">Carli files new records at the Lead stage.</p>
              </div>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-lg p-2 text-ink/45 hover:bg-cream hover:text-ink" aria-label="Close" data-id="element-1754" data-mp="38y92ccy5x2ms"><XIcon className="h-4 w-4" data-id="element-1755" data-mp="2zvzpvhjc2z6r" /></button>
            </div>

            <div className="mt-5 space-y-3" data-id="element-1756" data-mp="2zw9htanxvgf3">
              <Field label="Company name" value={draft.name} onChange={value => setDraft(current => ({
            ...current,
            name: value
          }))} required data-id="element-1757" data-mp="3nl2a8x5cr64y" />
              <Field label="Domain" value={draft.domain} onChange={value => setDraft(current => ({
            ...current,
            domain: value
          }))} placeholder="brand.com" data-id="element-1758" data-mp="3rmdzh1kbdpcm" />
              <div data-id="element-1759" data-mp="3iu2alk4um9yr">
                <label htmlFor="company-type" className="text-xs font-semibold text-ink/60" data-id="element-1760" data-mp="3itkh2v9km9uj">Category</label>
                <select id="company-type" value={draft.type} onChange={event => setDraft(current => ({
              ...current,
              type: event.target.value as CompanyType
            }))} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none focus:border-[#BB5E40]/35" data-id="element-1761" data-mp="adhcwzho33ay">
                  {companyTypes.map(item => <option key={item} value={item} data-id="element-1762" data-mp="21h12nwy82x4k">{item}</option>)}
                </select>
              </div>
              <div data-id="element-1763" data-mp="1wi69c12mpq67">
                <label htmlFor="company-owner" className="text-xs font-semibold text-ink/60" data-id="element-1764" data-mp="2u7j5cwv1awhg">Owner</label>
                <select id="company-owner" value={draft.owner} onChange={event => setDraft(current => ({
              ...current,
              owner: event.target.value
            }))} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none focus:border-[#BB5E40]/35" data-id="element-1765" data-mp="2p86iic45xpev">
                  {['Meris Butler', 'Meg Sawtelle', 'Rich Amsinger'].map(item => <option key={item} value={item} data-id="element-1766" data-mp="14r9yt741jxm3">{item}</option>)}
                </select>
              </div>
              <Field label="Location" value={draft.location} onChange={value => setDraft(current => ({
            ...current,
            location: value
          }))} placeholder="City, Country" data-id="element-1767" data-mp="3ge3megwlpv1c" />
              <Field label="Estimated opportunity" value={draft.value} onChange={value => setDraft(current => ({
            ...current,
            value
          }))} placeholder="$25,000" data-id="element-1768" data-mp="3pg347bffx35c" />
            </div>

            <div className="mt-6 flex gap-2" data-id="element-1769" data-mp="32384wk12edf">
              <button type="submit" className="flex-1 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-1770" data-mp="33edv1x19w76">Create company</button>
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-full border border-ink/15 px-4 py-2.5 text-sm font-semibold text-ink/65 hover:bg-cream" data-id="element-1771" data-mp="c2i0gfvestqf">Cancel</button>
            </div>
          </form>
        </div>}
    </div>;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  required
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const id = label.toLocaleLowerCase().replace(/\s+/g, '-');
  return <div data-id="element-1772" data-mp="2971691cmne99">
      <label htmlFor={id} className="text-xs font-semibold text-ink/60" data-id="element-1773" data-mp="296rfhbjrd32i">{label}</label>
      <input id={id} value={value} required={required} placeholder={placeholder} onChange={event => onChange(event.target.value)} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-sm text-ink outline-none placeholder:text-ink/35 focus:border-[#BB5E40]/35" data-id="element-1774" data-mp="1udz0mmc81wbg" />
    </div>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof BuildingIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1775" data-mp="2i7h7031zk7af">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1776" data-mp="12nkqn17bx452" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1777" data-mp="16riqxf8e4kzp">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1778" data-mp="16o4qicjaeluw">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1779" data-mp="z8rn598fscv8">{detail}</p>
    </article>;
}